<template>
  <article class="v2-profile-tab">
    <header class="v2-profile-tab__header">
      <div class="v2-profile-tab__header-text">
        <SectionHeader
          title="Datos Fiscales"
          eyebrow="Mi Perfil"
          description="Los siguientes datos fiscales son los que el sistema mostrará en automático cada vez que desees facturar una compra. Asegúrate de que los datos sean correctos."
          size="display-md"
        />
      </div>
      <div v-if="!loading" class="v2-profile-tab__actions">
        <BaseButton
          v-if="!editing"
          variant="amber"
          size="md"
          @click="onEdit"
        >
          <template #icon-left>
            <v-icon size="16">mdi-pencil-outline</v-icon>
          </template>
          Editar
        </BaseButton>
        <template v-else>
          <BaseButton
            variant="ghost"
            size="sm"
            :disabled="saving"
            @click="onCancel"
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="sm"
            :loading="saving"
            :disabled="saving"
            @click="onSave"
          >
            <template #icon-left>
              <v-icon size="16">mdi-content-save</v-icon>
            </template>
            Guardar
          </BaseButton>
        </template>
      </div>
    </header>

    <div v-if="loading" class="v2-profile-tab__loading">
      <v-icon size="32" color="grey lighten-1">mdi-progress-clock</v-icon>
      <p class="v2-body v2-body--sm v2-body--muted">Cargando datos fiscales…</p>
    </div>

    <template v-else>
      <div class="v2-profile-tab__grid">
        <div v-for="field in fieldList" :key="field.key" class="v2-profile-tab__cell">
          <BaseSelect
            v-if="field.kind === 'select'"
            :value="form[field.key] || ''"
            :label="field.label"
            :options="field.options"
            :placeholder="`Selecciona ${field.label.toLowerCase()}…`"
            :disabled="!editing"
            :error="errors[field.key] || ''"
            @change="onChange(field.key, $event)"
          />
          <BaseInput
            v-else
            :value="form[field.key] || ''"
            :label="field.label"
            :type="field.type || 'text'"
            :placeholder="field.placeholder || ''"
            :disabled="!editing"
            :error="errors[field.key] || ''"
            @input="onChange(field.key, $event)"
          />
        </div>
      </div>
    </template>
  </article>
</template>

<script>
import Vue from "vue";
import { mapActions, mapMutations } from "vuex";
import SectionHeader from "@/components/memberDashboardV2/ui/SectionHeader.vue";
import BaseInput from "@/components/memberDashboardV2/ui/BaseInput.vue";
import BaseSelect from "@/components/memberDashboardV2/ui/BaseSelect.vue";
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";

const EMPTY_FISCAL_DATA = {
  legalName: "",
  taxId: "",
  email: "",
  rf: "",
  zipCode: "",
  street: "",
  exterior: "",
  interior: "",
  neighborhood: "",
  municipality: "",
  state: "",
};

const FIELD_DEFINITIONS = [
  { key: "legalName", label: "Razón Social" },
  { key: "taxId", label: "RFC", type: "text", placeholder: "XAXX010101000" },
  { key: "email", label: "Correo Electrónico", type: "email" },
  { key: "rf", label: "Régimen Fiscal", kind: "select" },
  { key: "zipCode", label: "Código Postal", type: "text" },
  { key: "street", label: "Calle" },
  { key: "exterior", label: "Número Exterior" },
  { key: "interior", label: "Número Interior" },
  { key: "neighborhood", label: "Colonia" },
  { key: "municipality", label: "Municipio o delegación" },
  { key: "state", label: "Estado" },
];

export default {
  name: "FiscalDataTab",
  components: { SectionHeader, BaseInput, BaseSelect, BaseButton },
  props: {
    user: { type: Object, default: null },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      editing: false,
      editingForm: { ...EMPTY_FISCAL_DATA },
      errors: {},
      fiscalRegimenItems: [
        "601 - General de Ley Personas Morales",
        "603 - Personas Morales con Fines no Lucrativos",
        "605 - Sueldos y Salarios e Ingresos Asimilados a Salarios",
        "606 - Arrendamiento",
        "607 - Régimen de Enajenación o Adquisición de Bienes",
        "608 - Demás ingresos que obtengan las personas físicas",
        "609 - Consolidación",
        "610 - Residentes en el Extranjero sin Establecimiento Permanente en México",
        "611 - Ingresos por Dividendos (socios y accionistas)",
        "612 - Personas Físicas con Actividades Empresariales y Profesionales",
        "614 - Ingresos por intereses",
        "615 - Régimen de los ingresos por obtención de premios",
        "616 - Sin obligaciones fiscales",
        "620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos",
        "621 - Incorporación Fiscal",
        "622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras",
        "623 - Opcional para Grupos de Sociedades",
        "624 - Coordinados",
        "625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas",
        "626 - Régimen Simplificado de Confianza",
      ],
    };
  },
  computed: {
    form() {
      return this.editing ? this.editingForm : (this.user && this.user.fiscalData) || EMPTY_FISCAL_DATA;
    },
    fieldList() {
      return FIELD_DEFINITIONS.map((f) => {
        if (f.kind === "select") {
          return { ...f, options: this.fiscalRegimenItems };
        }
        return f;
      });
    },
  },
  methods: {
    ...mapMutations("notification", ["show"]),
    ...mapActions("user", ["fetchProfileData"]),
    onEdit() {
      this.editingForm = { ...EMPTY_FISCAL_DATA, ...((this.user && this.user.fiscalData) || {}) };
      this.editing = true;
      this.errors = {};
    },
    onCancel() {
      this.editing = false;
      this.errors = {};
    },
    onChange(key, value) {
      if (this.editing) {
        this.editingForm = { ...this.editingForm, [key]: value };
      }
    },
    async onSave() {
      const validation = this.validate();
      if (!validation.ok) {
        this.errors = validation.errors;
        return;
      }
      try {
        await Vue.axios({
          method: "PUT",
          url: `/members/form/${this.user._id}`,
          data: { fiscalData: this.editingForm },
        });
        await this.fetchProfileData();
        this.editing = false;
        this.errors = {};
        this.show({
          text: "Datos fiscales guardados correctamente",
          color: "success",
        });
      } catch (error) {
        this.show({
          text:
            (error && error.response && error.response.data &&
              (error.response.data.error?.message || error.response.data.message)) ||
            "No se pudieron guardar los datos fiscales",
          color: "error",
        });
      }
    },
    validate() {
      const errors = {};
      const data = this.editingForm;
      if (data.taxId && (data.taxId.length < 12 || data.taxId.length > 13)) {
        errors.taxId = "El RFC debe tener 12 o 13 caracteres";
      }
      if (data.zipCode && data.zipCode.length !== 5) {
        errors.zipCode = "El código postal debe tener 5 caracteres";
      }
      if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        errors.email = "Correo electrónico no válido";
      }
      return { ok: Object.keys(errors).length === 0, errors };
    },
  },
};
</script>

<style scoped>
.v2-profile-tab {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
}

.v2-profile-tab__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  border-bottom: 1px solid var(--color-border);
  padding-bottom: var(--space-3);
  flex-wrap: wrap;
}

.v2-profile-tab__header-text {
  flex: 1 1 auto;
  min-width: 0;
}

.v2-profile-tab__actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

.v2-profile-tab__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-8);
}

.v2-profile-tab__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4) var(--space-6);
  background-color: var(--color-neutral-100);
  padding: var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
}

@media (min-width: 768px) {
  .v2-profile-tab__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .v2-profile-tab__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.v2-profile-tab__cell {
  min-width: 0;
}
</style>